import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import {
  ArrowLeft, Phone, Globe, Crown, Car, Sparkles, MapPin, Share2, Heart, QrCode, Copy, MessageCircle, Cigarette, Loader2, Check, Volume2, VolumeX, Instagram, Wallet, Footprints, ShieldCheck, ChevronDown, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { OwnerDashboardToggle, OwnerEditButton, BackToHomeFooter } from "../components/OwnerToolbar";
import EditSheet from "../components/EditSheet";
import { api, getMe } from "../lib/api";
import { categoryLabel, haversineKm, fmtDistanceWalk } from "../lib/geo";
import { useGps } from "../lib/useGps";
import { getCachedBusiness, isOnline } from "../lib/offline";
import { findCachedBusinessById } from "../lib/businessCache";
import { Stars, OpenBadge } from "../components/PlaceCard";
import { CategoryLabel } from "../components/CategoryLabel";
import { BottomNav } from "../components/BottomNav";
import { ReviewsSection } from "../components/Reviews";
import OpenInMapsSheet from "../components/OpenInMapsSheet";
import LogSpendModal from "../components/LogSpendModal";
import SpendHistoryStrip from "../components/SpendHistoryStrip";
import GuestGate from "../components/GuestGate";
import * as guestStore from "../lib/guestStorage";
import { useWhatsAppSheet } from "../components/WhatsAppActionSheet";
import { sendRideRequest, sendBusinessContact } from "../lib/sendRideRequest";
import { openWhatsApp, shareViaWhatsApp } from "../lib/openWhatsApp";
import { formatPhoneWithFlag } from "../lib/formatPhone";
import { useFeatureFlag } from "../lib/featureFlags";
import DriverQRModal from "../components/DriverQRModal";
import HeartButton from "../components/HeartButton";
import { useServiceNumbers, canShowPlatformService, canShowBusinessVIP, canShowBusinessContact, platformNumberFor } from "../lib/whatsapp";
import { useI18n } from "../lib/i18n";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const cleanPhone = (s) => (s || "").replace(/\D/g, "");

export default function PlaceProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const platformNums = useServiceNumbers();
  const { t: tI18n, lang } = useI18n();
  const { openWA } = useWhatsAppSheet();
  const [b, setB] = useState(null);
  const [offlineMiss, setOfflineMiss] = useState(false);
  const { coords: gpsCoords, state: gpsState, requestGps } = useGps();
  const [user, setUser] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const [rideOpen, setRideOpen] = useState(false);
  const [vipOpen, setVipOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [mapsOpen, setMapsOpen] = useState(false);
  const [spendOpen, setSpendOpen] = useState(false);
  const [spendRefresh, setSpendRefresh] = useState(0);
  const [dirOpen, setDirOpen] = useState(false);
  const [trackSpendOn, setTrackSpendOn] = useState(false);
  const logSpendEnabled = useFeatureFlag("log_spend_enabled");
  const [guestGate, setGuestGate] = useState({ open: false, action: null });
  const [canReview, setCanReview] = useState(false);
  const [checkingIn, setCheckingIn] = useState(false);
  const [justCheckedIn, setJustCheckedIn] = useState(false);
  const [vipReminder, setVipReminder] = useState(false);

  useEffect(() => {
    // iter122 — Check offline / cache first to avoid iOS "Cellular Data
    // Turn Off" dialog and immediately render last-known data.
    const cachedOffline = findCachedBusinessById(id) || getCachedBusiness(id);
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      if (cachedOffline) setB(cachedOffline);
      else setOfflineMiss(true);
    } else {
      // Optimistic seed from cache so the page is never blank while the API resolves.
      if (cachedOffline && !b) setB(cachedOffline);
      api.get(`/businesses/${id}`)
        .then(async ({ data }) => {
          // iter216 — Merge in hero_bg_url as the fallback banner
          // when the business hasn't uploaded a custom one.
          if (!data.background_image) {
            try {
              const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public-settings`);
              const s = await r.json();
              if (s.hero_bg_url) data.background_image = s.hero_bg_url;
            } catch { /* offline-tolerant */ }
          }
          setB(data);
        })
        .catch(() => {
          const cached = findCachedBusinessById(id) || getCachedBusiness(id);
          if (cached) setB(cached);
          else if (!isOnline()) setOfflineMiss(true);
          else navigate("/");
        });
    }
    getMe().then((me) => {
      setUser(me);
      if (me) {
        // Skip authed API calls when offline — cache them as empty.
        if (typeof navigator !== "undefined" && navigator.onLine === false) {
          setSaved(guestStore.isSaved(id, "business"));
          return;
        }
        api.get("/favorites").then(({ data }) => {
          setSaved((Array.isArray(data) ? data : []).some((f) => f.target_id === id && f.target_type === "business"));
        }).catch(() => {});
        api.get("/me/vip-reminders").then(({ data }) => {
          setVipReminder((Array.isArray(data) ? data : []).some((r) => r.business_id === id));
        }).catch(() => {});
      } else {
        // Guest: read from localStorage
        setSaved(guestStore.isSaved(id, "business"));
        setVipReminder(guestStore.hasVipReminder(id));
      }
    }).catch(() => {});
    if (typeof navigator !== "undefined" && navigator.onLine !== false) {
      api.get(`/me/can-review?target_id=${id}&target_type=business`)
        .then(({ data }) => setCanReview(!!data?.can_review))
        .catch(() => setCanReview(false));
    }
  }, [id, navigate]);

  const toggleVipReminder = async () => {
    if (!user) {
      const next = guestStore.toggleVipReminder(id);
      setVipReminder(next);
      toast.success(next ? "Saved to your VIP reminders" : "Removed");
      return;
    }
    const next = !vipReminder;
    setVipReminder(next);
    try {
      await api.post(`/businesses/${id}/vip-reminder`);
    } catch (e) {
      setVipReminder(!next);
      toast.error(e.response?.data?.detail || "Could not toggle");
    }
  };

  const toggleSave = async () => {
    if (!user) {
      const next = guestStore.toggleSaved(id, "business");
      setSaved(next);
      toast.success(next ? "Saved" : "Removed");
      return;
    }
    const prev = saved;
    setSaved(!prev);
    try {
      await api.post("/favorites/toggle", { target_id: id, target_type: "business" });
    } catch {
      setSaved(prev);
      toast.error("Couldn't save");
    }
  };

  const checkIn = async () => {
    if (checkingIn || justCheckedIn) return;
    setCheckingIn(true);
    try {
      if (!user) {
        const r = guestStore.appendCheckin({ target_type: "business", target_id: id, target_name: b?.name || null });
        if (r.duplicate) {
          setJustCheckedIn(true);
          toast.info(lang === "es" ? "Ya registrado recientemente" : "Already checked in recently");
        } else if (r.ok) {
          setJustCheckedIn(true);
          setSaved(true);
          if (!guestStore.isSaved(id, "business")) guestStore.toggleSaved(id, "business");
          toast.success(lang === "es" ? `Registro en ${b?.name || "este lugar"}` : `Checked in to ${b?.name || "this place"}`);
          setTimeout(() => setJustCheckedIn(false), 30 * 60 * 1000);
        }
        return;
      }
      const { data } = await api.post("/check-ins", { business_id: id });
      if (data?.ok) {
        setJustCheckedIn(true);
        setSaved(true);
        toast.success(data.message || `Checked in to ${b?.name || "this place"}`);
        // Refresh review eligibility — manual check-in now qualifies for a review
        api.get(`/me/can-review?target_id=${id}&target_type=business`)
          .then(({ data: r }) => setCanReview(!!r?.can_review))
          .catch(() => {});
        // Re-arm the button after 30 min duplicate window
        setTimeout(() => setJustCheckedIn(false), 30 * 60 * 1000);
      } else if (data?.type === "duplicate") {
        setJustCheckedIn(true);
        toast.info(data.message || "Already checked in here recently");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Check-in failed");
    } finally {
      setCheckingIn(false);
    }
  };

  if (offlineMiss) {
    return (
      <main className="min-h-screen pt-24 px-6 bg-background" data-testid="place-offline-miss">
        <div className="max-w-md mx-auto text-center lux-card p-8">
          <div className="eyebrow">Offline</div>
          <h1 className="font-display text-3xl mt-2 text-foreground">Not in your offline cache</h1>
          <p className="mt-3 text-foreground/65 text-sm">This place isn&apos;t in your offline cache. Connect to the internet to view it.</p>
          <Link to="/" className="mt-6 btn-outline py-2 px-4 text-xs inline-flex">Back home</Link>
        </div>
      </main>
    );
  }
  if (!b) return null;

  const photos = Array.isArray(b.photos) ? b.photos : [];
  const FALLBACK_HERO = {
    cigar_lounge: "https://images.unsplash.com/photo-1527661591475-527312dd65f5?w=1800&q=85&auto=format&fit=crop",
    rooftop:      "https://images.unsplash.com/photo-1517457210348-703079e57d4b?w=1800&q=85&auto=format&fit=crop",
    club:         "https://images.unsplash.com/photo-1572116469696-31de0f17cc34?w=1800&q=85&auto=format&fit=crop",
    bar:          "https://images.unsplash.com/photo-1470337458703-46ad1756a187?w=1800&q=85&auto=format&fit=crop",
    restaurant:   "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1800&q=85&auto=format&fit=crop",
    hotel:        "https://images.unsplash.com/photo-1455587734955-081b22074882?w=1800&q=85&auto=format&fit=crop",
    beach:        "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1800&q=85&auto=format&fit=crop",
    shop:         "https://images.unsplash.com/photo-1567521464027-f127ff144326?w=1800&q=85&auto=format&fit=crop",
  };
  const fallback = FALLBACK_HERO[b.category] || FALLBACK_HERO.cigar_lounge;

  return (
    <main data-testid="place-profile" className="min-h-screen pb-32 bg-background">
      {/* Hero gallery — swipeable carousel, capped at 240px on mobile */}
      <PhotoGallery
        photos={photos}
        fallback={fallback}
        businessName={b.name}
        category={b.category}
        backTo="/"
        saved={saved}
        onToggleSave={toggleSave}
      />

      {/* Title block */}
      <section className="mt-6 relative z-10 px-6">
        {/* iter216 — Owner-only DASHBOARD toggle. Rendered only when the
            current user owns this business row. */}
        <OwnerDashboardToggle
          isOwner={!!(user && b && user.user_id === b.owner_user_id)}
          ownerDashboardPath="/business"
        />
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-3">
              <OpenBadge business={b} />
              {b.vip_enabled && (
                <span className="pill pill-vip">
                  <Crown className="w-3 h-3" strokeWidth={2} /> VIP
                </span>
              )}
              <OwnerEditButton
                isOwner={!!(user && b && user.user_id === b.owner_user_id)}
                onClick={() => setEditOpen(true)}
              />
            </div>
            <h1 className="font-display text-5xl sm:text-6xl text-foreground leading-[0.95] tracking-tight" data-testid="place-name">
              {b.name}
            </h1>
            <div className="eyebrow-faded mt-3">
              <CategoryLabel category={b.category} subcategory={b.subcategory} />
              {b.neighborhood ? ` · ${b.neighborhood}` : ""}
            </div>
            {b.whatsapp && (
              <button
                type="button"
                data-testid="place-whatsapp-chip"
                onClick={() => {
                  api.post("/whatsapp/interaction", { target_id: b.id, target_type: "business", kind: "contact_business" }).catch(() => {});
                  sendBusinessContact(
                    { name: b.name, address: b.address, lat: b.lat, lng: b.lng, whatsapp: b.whatsapp, phone: b.phone },
                    { businessId: b.id, source: "place-profile-header-chip" },
                  );
                }}
                className="inline-flex items-center gap-2 mt-3 transition-colors hover:brightness-110"
                style={{
                  minHeight: 32,
                  padding: "6px 12px",
                  background: "rgba(37,211,102,0.10)",
                  border: "1px solid rgba(37,211,102,0.35)",
                  borderRadius: 999,
                  color: "#25d366",
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                <WaIcon />
                <span>WhatsApp</span>
              </button>
            )}
            {(() => {
              if (b.lat == null || b.lng == null) return null;
              if (gpsCoords) {
                const dist = fmtDistanceWalk(haversineKm(gpsCoords, { lat: b.lat, lng: b.lng }));
                if (!dist) return null;
                return (
                  <div
                    className="mt-2 inline-flex items-center gap-1.5"
                    style={{ color: "#B8B2A7", fontFamily: "'DM Sans', sans-serif", fontSize: 13 }}
                    data-testid="place-distance"
                  >
                    <Footprints className="w-3.5 h-3.5" strokeWidth={1.5} />
                    <span>{dist}</span>
                  </div>
                );
              }
              if (gpsState === "denied" || gpsState === "idle") {
                return (
                  <button
                    type="button"
                    onClick={requestGps}
                    data-testid="place-enable-location"
                    className="mt-2 inline-flex items-center gap-1 hover:opacity-80 transition-opacity"
                    style={{ color: "#E8B84B", fontFamily: "'DM Sans', sans-serif", fontSize: 11 }}
                  >
                    <MapPin className="w-3 h-3" strokeWidth={1.5} /> Enable location
                  </button>
                );
              }
              return null;
            })()}
            {b.hidden_from_discovery && (
              <div
                data-testid="place-inactivity-banner"
                className="mt-4 rounded-md px-4 py-3 flex items-start gap-2.5"
                style={{
                  background: "rgba(245,158,11,0.08)",
                  border: "1px solid rgba(245,158,11,0.4)",
                  color: "#fcd34d",
                  fontFamily: "'DM Sans', sans-serif", fontSize: 13,
                }}
              >
                <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.5} />
                <span>{tI18n("listing_inactive_warning")}</span>
              </div>
            )}
            {b.ratings_visible && b.ratings_average != null && (
              <div className="mt-4 flex items-center gap-2.5" data-testid="place-rating">
                <Stars value={b.ratings_average} size={14} />
                <span className="text-sm text-foreground/75 font-mono">
                  {b.ratings_average.toFixed(1)} <span className="text-foreground/45">({b.approved_review_count ?? b.ratings_count})</span>
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Description */}
        {b.description && (
          <p className="mt-7 text-foreground/75 leading-[1.7] font-display-light text-lg max-w-prose" data-testid="place-description">
            {b.description}
          </p>
        )}

        <div className="rule-soft my-10" />

        {/* iter104 — Action buttons: 4 rows */}
        <div className="space-y-3" data-testid="place-actions">
          {/* Row 1 — Primary: Request a Ride (gold gradient, prominent) */}
          <button
            type="button"
            data-testid="action-request-ride"
            onClick={() => sendRideRequest(
              { name: b.name, address: b.address, lat: b.lat, lng: b.lng, category: b.category, neighborhood: b.neighborhood },
              { businessId: b.id, source: "place-profile-ride" },
            )}
            className="w-full inline-flex items-center justify-center gap-2 hover:opacity-95 transition-opacity"
            style={{
              height: 52,
              background: "linear-gradient(180deg, #F5CC55, #E8B84B)",
              borderRadius: 12,
              color: "#0B0B0B",
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 16,
              fontWeight: 700,
              border: "none",
              boxShadow: "0 4px 16px rgba(232,184,75,0.35)",
            }}
          >
            <Car style={{ width: 18, height: 18 }} strokeWidth={2} />
            {tI18n("ride_request_driver")}
          </button>

          {/* Row 2 — View Directions · Contact */}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              data-testid="action-view-directions"
              onClick={() => setDirOpen(true)}
              disabled={b.lat == null || b.lng == null}
              className="inline-flex items-center justify-center gap-1.5 transition-colors hover:bg-white/[0.04]"
              style={{
                height: 44, borderRadius: 10, padding: "0 10px",
                background: "transparent",
                color: "#E8B84B",
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 13, fontWeight: 600,
                border: "1.5px solid rgba(232,184,75,0.55)",
                opacity: (b.lat == null || b.lng == null) ? 0.5 : 1,
              }}
            >
              <QrCode className="w-4 h-4" strokeWidth={2} /> {tI18n("ride_view_directions")}
            </button>
            {(b.whatsapp || b.phone) ? (
              <button
                type="button"
                data-testid="action-contact-business"
                onClick={() => {
                  api.post("/whatsapp/interaction", { target_id: b.id, target_type: "business", kind: "contact_business" }).catch(() => {});
                  sendBusinessContact(
                    { name: b.name, address: b.address, lat: b.lat, lng: b.lng, whatsapp: b.whatsapp, phone: b.phone },
                    { businessId: b.id, source: "place-profile-contact" },
                  );
                }}
                className="inline-flex items-center justify-center gap-1.5 transition-colors hover:bg-white/[0.04]"
                style={{
                  height: 44, borderRadius: 10, padding: "0 10px",
                  background: "transparent",
                  color: "#25D366",
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 13, fontWeight: 600,
                  border: "1px solid rgba(37,211,102,0.4)",
                }}
              >
                <MessageCircle className="w-4 h-4" strokeWidth={2} /> {tI18n("contact_btn")}
              </button>
            ) : <div />}
          </div>

          {/* Row 3 — Check In · Request VIP */}
          <div className="grid grid-cols-2 gap-2">
            <button
              data-testid="action-check-in"
              onClick={checkIn}
              disabled={checkingIn || justCheckedIn}
              className="inline-flex items-center justify-center gap-1.5 transition-colors hover:bg-white/[0.04]"
              style={{
                height: 44, borderRadius: 10, padding: "0 10px",
                background: "transparent",
                color: justCheckedIn ? "#E8B84B" : "#E8B84B",
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 13, fontWeight: 600,
                border: `1.5px solid rgba(232,184,75,${justCheckedIn ? "0.7" : "0.55"})`,
                opacity: checkingIn ? 0.6 : 1,
              }}
            >
              {justCheckedIn
                ? <><Check className="w-4 h-4" strokeWidth={2} /> Checked in</>
                : checkingIn
                  ? <><Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> …</>
                  : <><MapPin className="w-4 h-4" strokeWidth={2} /> Check In</>}
            </button>
            {canShowBusinessVIP(b) ? (
              <button
                data-testid="action-request-vip"
                onClick={async () => {
                  if (!user) {
                    openWA({ type: "vip_request", destination: b, businessId: id, source: "place-vip-request" });
                    return;
                  }
                  if (!vipReminder) {
                    try {
                      await api.post(`/businesses/${id}/vip-reminder`);
                      setVipReminder(true);
                    } catch { /* swallow */ }
                  }
                  setVipOpen(true);
                }}
                className="inline-flex items-center justify-center gap-1.5 transition-colors hover:bg-white/[0.04]"
                style={{
                  height: 44, borderRadius: 10, padding: "0 10px",
                  background: "transparent",
                  color: "#E8B84B",
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 13, fontWeight: 600,
                  border: "1.5px solid rgba(232,184,75,0.55)",
                }}
              >
                <Crown className="w-4 h-4" strokeWidth={2} /> Request VIP
              </button>
            ) : <div />}
          </div>

          {/* Row 4 — Track Spend toggle row (admin-gated by log_spend_enabled) */}
          {logSpendEnabled && (
            <div
              className="flex items-center justify-between gap-3"
              data-testid="place-track-spend-row"
              style={{
                background: "rgba(20,20,20,0.6)",
                border: "1px solid rgba(232,184,75,0.18)",
                borderRadius: 12,
                padding: "10px 12px",
              }}
            >
              <span
                className="grid place-items-center shrink-0"
                style={{
                  width: 28, height: 28, borderRadius: 999,
                  background: "rgba(232,184,75,0.08)",
                  color: "#E8B84B",
                }}
              >
                <Wallet className="w-3.5 h-3.5" strokeWidth={1.5} />
              </span>
              <div className="flex-1 min-w-0">
                <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#F5F1E8", fontWeight: 500 }}>
                  {tI18n("track_spend_title")}
                </div>
                <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#B0AAA3" }}>
                  {tI18n("track_spend_sub")}
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={trackSpendOn}
                data-testid="action-log-spend"
                onClick={() => {
                  if (trackSpendOn) {
                    if (window.confirm(tI18n("log_spend_remove_confirm"))) setTrackSpendOn(false);
                  } else {
                    setTrackSpendOn(true);
                    setSpendOpen(true);
                  }
                }}
                className="shrink-0 relative transition-colors"
                style={{
                  width: 44, height: 26, borderRadius: 999,
                  background: trackSpendOn ? "#E8B84B" : "#6a6a6a",
                  border: "none", padding: 0, cursor: "pointer",
                }}
              >
                <span
                  style={{
                    position: "absolute", top: 3, left: trackSpendOn ? 21 : 3,
                    width: 20, height: 20, borderRadius: 999, background: "#fff",
                    transition: "left 0.18s ease", boxShadow: "0 1px 3px rgba(0,0,0,0.4)",
                  }}
                />
              </button>
            </div>
          )}
        </div>

        {logSpendEnabled && trackSpendOn && (
          <SpendHistoryStrip
            user={user}
            targetType="business"
            targetId={id}
            refreshKey={spendRefresh}
            onTap={() => setSpendOpen(true)}
          />
        )}

        <DriverQRModal
          open={dirOpen}
          onClose={() => setDirOpen(false)}
          business={b}
          testidPrefix="place-directions"
        />

        <div className="rule my-8" />

        {/* iter69 — Hours badge with expandable list */}
        {(b.hours || b.opening_hours) && (
          <HoursBadge hours={b.hours} openingHours={b.opening_hours} tI18n={tI18n} />
        )}

        {/* iter69 — Contact rows */}
        <div className="mt-6" data-testid="place-contact-rows">
          {b.address && (
            <ContactRow
              icon={<MapPin className="w-4 h-4" strokeWidth={1.5} />}
              iconBg="rgba(232,184,75,0.08)"
              iconColor="#E8B84B"
              label={tI18n("pp_label_address")}
              value={b.address}
              actionLabel={tI18n("pp_action_copy")}
              onAction={async () => { try { await navigator.clipboard.writeText(b.address); toast.success(tI18n("pp_copied")); } catch { /* ignore */ } }}
              testId="contact-row-address"
            />
          )}
          {b.phone && (
            <ContactRow
              icon={<Phone className="w-4 h-4" strokeWidth={1.5} />}
              iconBg="rgba(232,184,75,0.08)"
              iconColor="#E8B84B"
              label={tI18n("pp_label_phone")}
              value={formatPhoneWithFlag(b.phone)}
              actionLabel={tI18n("pp_action_call")}
              href={`tel:${b.phone}`}
              testId="contact-row-phone"
            />
          )}
          {b.whatsapp && (
            <ContactRow
              icon={<WaIcon />}
              iconBg="rgba(37,211,102,0.1)"
              iconColor="#25d366"
              label="WHATSAPP"
              value={formatPhoneWithFlag(b.whatsapp)}
              actionLabel={tI18n("pp_action_chat")}
              actionGreen
              onAction={() => sendBusinessContact(b, { businessId: b.id, source: "place-profile-contact-row" })}
              testId="contact-row-whatsapp"
            />
          )}
          {b.instagram && (
            <ContactRow
              icon={<Instagram className="w-4 h-4" strokeWidth={1.5} />}
              iconBg="rgba(232,184,75,0.08)"
              iconColor="#E8B84B"
              label="INSTAGRAM"
              value={`@${(b.instagram || "").replace(/^@/, "")}`}
              actionLabel={tI18n("pp_action_view")}
              href={`https://instagram.com/${(b.instagram || "").replace(/^@/, "")}`}
              testId="contact-row-instagram"
            />
          )}
          {b.website && (
            <ContactRow
              icon={<Globe className="w-4 h-4" strokeWidth={1.5} />}
              iconBg="rgba(232,184,75,0.08)"
              iconColor="#E8B84B"
              label="WEBSITE"
              value={b.website}
              actionLabel={tI18n("pp_action_open")}
              href={b.website}
              testId="contact-row-website"
            />
          )}
        </div>

        {/* Legacy smoking-policy row preserved (no new equivalent) */}
        <dl className="mt-4 space-y-4 text-sm">
          {b.smoking_policy && <DetailRow label="Smoking" icon={Cigarette} value={b.smoking_policy.replaceAll("_", " ")} />}
        </dl>
      </section>

      <ReviewsSection
        targetId={b.id}
        targetType="business"
        targetName={b.name}
        canReview={canReview && !!user}
        ratingsVisible={b.ratings_visible}
        approvedCount={b.approved_review_count}
      />

      {/* iter69 — Nearby slider */}
      <NearbySection bizId={b.id} category={b.category} neighborhood={b.neighborhood} tI18n={tI18n} userCoords={gpsCoords} />

      <RideDialog open={rideOpen} onOpenChange={setRideOpen} business={b} />
      <VipDialog open={vipOpen} onOpenChange={setVipOpen} business={b} />
      <ShareDialog open={shareOpen} onOpenChange={setShareOpen} business={b} />
      <OpenInMapsSheet open={mapsOpen} onClose={() => setMapsOpen(false)} business={b} testidPrefix="place-maps" />
      <LogSpendModal
        open={spendOpen}
        onOpenChange={setSpendOpen}
        targetType="business"
        targetId={id}
        targetName={b.name}
        onSaved={() => setSpendRefresh((n) => n + 1)}
      />
      <GuestGate
        open={guestGate.open}
        onOpenChange={(v) => setGuestGate({ open: v, action: v ? guestGate.action : null })}
        action={guestGate.action}
      />

      <BackToHomeFooter />
      <BottomNav />
      <EditSheet
        open={editOpen}
        onClose={() => setEditOpen(false)}
        kind="business"
        profile={b}
        onSaved={(patched) => setB((prev) => ({ ...prev, ...patched }))}
      />
    </main>
  );
}

function DetailRow({ label, icon: Icon, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="w-4 h-4 text-gold mt-0.5 shrink-0" strokeWidth={1.5} />
      <div>
        <div className="eyebrow mb-0.5">{label}</div>
        <div className="text-foreground/85">{value}</div>
      </div>
    </div>
  );
}

// iter87: RideDialog removed — Request Ride buttons fire sendRideRequest()
// directly. The legacy <RideDialog /> mount above is harmless (rideOpen never
// flips true anymore) but we keep the no-op stub so future grep history makes
// the iter83→iter87 evolution legible.
function RideDialog() { return null; }

function VipDialog({ open, onOpenChange, business }) {
  const [guests, setGuests] = useState("2");
  const [time, setTime] = useState("Tonight, 9pm");
  const send = () => {
    const msg =
      `Hi, I would like to request VIP access through Habano.app.\n` +
      `Business: ${business.name}\n` +
      `Customers: ${guests}\n` +
      `Requested time: ${time}`;
    const phone = cleanPhone(business.whatsapp);
    if (!phone) { toast.error("Business hasn't shared a WhatsApp number"); return; }
    // iter139 — Open WhatsApp first (synchronous, inside the click handler)
    // so iOS Safari / Chrome on Android don't block the popup. Logging
    // runs after as a fire-and-forget side effect.
    const openedUrl = openWhatsApp(phone, msg);
    if (!openedUrl) {
      toast.error("Could not open WhatsApp");
      return;
    }
    api.post("/whatsapp/interaction", { target_id: business.id, target_type: "business", kind: "request_vip" }).catch(() => {});
    api.post("/whatsapp-activity", {
      service_type: "vip_business",
      related_business_id: business.id,
      related_business_name: business.name,
      full_message: msg,
      language: "en",
    }).catch(() => {});
    onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="vip-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-foreground">Request VIP</DialogTitle>
          <DialogDescription>Opens WhatsApp with a pre-filled VIP request to {business.name}.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <SmallField label="Customers">
            <Select value={guests} onValueChange={setGuests}>
              <SelectTrigger data-testid="vip-guests"><SelectValue /></SelectTrigger>
              <SelectContent>{[2, 4, 6, 8, 10, 12].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
            </Select>
          </SmallField>
          <SmallField label="Requested time">
            <input data-testid="vip-time" value={time} onChange={(e) => setTime(e.target.value)} className="form-input" />
          </SmallField>
        </div>
        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="btn-ghost">Cancel</button>
          <button data-testid="vip-send" onClick={send} className="px-4 py-2 bg-[#25D366] text-white text-sm inline-flex items-center gap-2 rounded-md hover:opacity-90">
            <MessageCircle className="w-4 h-4" strokeWidth={1.5} /> Open WhatsApp
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ShareDialog({ open, onOpenChange, business }) {
  const link = typeof window !== "undefined" ? `${window.location.origin}/places/${business.id}` : "";
  const logShare = (via) => {
    // Fire-and-forget — never block UX on telemetry.
    try {
      api.post("/business-shares", { business_id: business.id, shared_via: via });
    } catch { /* swallow */ }
  };
  const wa = () => {
    // iter139 — open first to keep the user-gesture stack intact.
    shareViaWhatsApp(`${business.name} on Habano.app — ${link}`);
    logShare("whatsapp");
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      logShare("link");
      toast.success("Link copied");
      onOpenChange(false);
    } catch { toast.error("Copy failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-foreground">Share {business.name}</DialogTitle>
          <DialogDescription>Send to a friend.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <button data-testid="share-wa" onClick={wa} className="flex-1 px-4 py-3 bg-[#25D366] text-white text-sm inline-flex items-center justify-center gap-2 rounded-md">
            <MessageCircle className="w-4 h-4" strokeWidth={1.5} /> WhatsApp
          </button>
          <button data-testid="share-copy" onClick={copy} className="flex-1 btn-outline py-3 text-sm">
            <Copy className="w-4 h-4" strokeWidth={1.5} /> Copy link
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SmallField({ label, children }) {
  return (
    <label className="block">
      <div className="eyebrow mb-1">{label}</div>
      {children}
    </label>
  );
}


/* ----------------------------------------------------------------------- */
/* PhotoGallery — swipeable carousel for the business photos.              */
/*   • Max height 240px on mobile (.place-profile-hero-wrap)               */
/*   • Dots + "1 / N" counter when >1 photo                                */
/*   • No autoplay — manual swipe only                                     */
/*   • Lazy load — only current and adjacent slides receive a src          */
/*   • One photo → renders normally with no dots/counter                   */
/*   • No photos → dark placeholder with category icon                     */
/* ----------------------------------------------------------------------- */
function PhotoGallery({ photos, fallback, businessName, category, backTo, saved, onToggleSave }) {
  // Normalize slides — each entry becomes { kind, src, youtube_id, poster }.
  // `src`/`poster` are always absolute URLs ready for use.
  const slides = useMemo(() => {
    const abs = (s) => (s && s.startsWith("http") ? s : `${process.env.REACT_APP_BACKEND_URL}${s || ""}`);
    return (photos || [])
      .map((p) => {
        if (!p) return null;
        if (p.media_type === "youtube" && p.youtube_id) {
          return {
            kind: "youtube",
            youtube_id: p.youtube_id,
            poster: abs(p.poster_url || `https://i.ytimg.com/vi/${p.youtube_id}/hqdefault.jpg`),
          };
        }
        const src = p.full || p.medium || p.thumbnail || p.url;
        if (!src) return null;
        return { kind: "image", src: abs(src) };
      })
      .filter(Boolean);
  }, [photos]);

  const [emblaRef, embla] = useEmblaCarousel({ loop: false, dragFree: false });
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (!embla) return;
    const onSelect = () => setActive(embla.selectedScrollSnap());
    embla.on("select", onSelect);
    onSelect();
    return () => embla.off("select", onSelect);
  }, [embla]);

  const hasMultiple = slides.length > 1;
  const isEmpty = slides.length === 0;

  return (
    <div className="place-profile-hero-wrap relative bg-[hsl(var(--surface-elevated))] overflow-hidden" data-testid="place-hero-gallery">
      {isEmpty ? (
        <PlaceholderHero category={category} />
      ) : !hasMultiple ? (
        <GallerySlide slide={slides[0]} active idx={0} businessName={businessName} markActiveHero category={category} />
      ) : (
        <div ref={emblaRef} className="overflow-hidden h-full" data-testid="hero-viewport">
          <div className="flex h-full">
            {slides.map((slide, i) => (
              <div key={i} className="flex-[0_0_100%] min-w-0 h-full" data-testid={`hero-slide-${i}`}>
                {/* Lazy: only render media for active ± 1 */}
                {Math.abs(i - active) <= 1 ? (
                  <GallerySlide slide={slide} active={i === active} idx={i} businessName={businessName} markActiveHero={i === active} category={category} />
                ) : (
                  <div className="w-full h-full bg-[#0a0a0a]" />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent pointer-events-none" />

      <Link
        to={backTo}
        data-testid="place-back"
        className="absolute top-5 left-5 w-11 h-11 rounded-full grid place-items-center glass-dark border border-gold-soft text-foreground hover:text-gold transition-colors duration-300 z-10"
        aria-label="Back"
      >
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
      </Link>
      <div className="absolute top-5 right-5 z-10">
        <HeartButton
          saved={saved}
          onToggle={onToggleSave}
          testid="place-save"
          showHint
          ariaLabel={saved ? "Saved" : "Save"}
        />
      </div>

      {hasMultiple && (
        <>
          <div
            className="absolute top-5 right-20 px-2.5 py-1 rounded-full text-[11px] font-mono text-white tracking-widest pointer-events-none z-10"
            style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
            data-testid="hero-counter"
          >
            {active + 1} / {slides.length}
          </div>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-10" data-testid="photo-dots">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => embla?.scrollTo(i)}
                aria-label={`Photo ${i + 1}`}
                data-testid={`hero-dot-${i}`}
                className={`h-1 rounded-full transition-all duration-500 ${
                  i === active ? "bg-gold w-8" : "bg-foreground/40 w-1.5"
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function PlaceholderHero({ category }) {
  // Match the place-profile-hero size via the wrapper; render the category icon
  // in gold as the empty-state visual instead of a stock photo.
  const Icon = (() => {
    switch (category) {
      case "cigar_lounge":
      case "shop":
        return Cigarette;
      case "rooftop":
      case "bar":
      case "club":
      case "restaurant":
        return Sparkles;
      default:
        return Crown;
    }
  })();
  return (
    <div className="place-profile-hero w-full grid place-items-center bg-[#0a0a0a]" data-testid="hero-placeholder">
      <Icon className="w-12 h-12 text-gold" strokeWidth={1.25} />
    </div>
  );
}


/* GallerySlide — renders one slide of the gallery as either an image or a
 * muted-autoplay YouTube embed. Active-only video playback to save bandwidth. */
function GallerySlide({ slide, active, idx, businessName, markActiveHero, category }) {
  const tid = markActiveHero ? "hero-photo" : undefined;
  const [errored, setErrored] = useState(false);
  // iter123 — Preflight: when offline, skip <img src> entirely to avoid
  // the iOS Cellular Data dialog. Render gradient + gold category icon.
  const isOffline = typeof navigator !== "undefined" && navigator.onLine === false;
  if (slide.kind === "youtube") {
    return (
      <YouTubeGallerySlide
        youtubeId={slide.youtube_id}
        poster={slide.poster}
        active={active}
        idx={idx}
        title={businessName}
        markActiveHero={markActiveHero}
      />
    );
  }
  if (errored || isOffline) {
    return (
      <div
        className="place-profile-hero w-full grid place-items-center"
        data-testid={tid || "hero-image-fallback"}
        style={{ background: "linear-gradient(135deg, #1a0f06, #3a1c04)" }}
      >
        <CategoryIcon category={category} className="w-12 h-12 text-gold" />
      </div>
    );
  }
  return (
    <img
      src={slide.src}
      alt={`${businessName} ${idx + 1}`}
      loading="lazy"
      className="place-profile-hero"
      data-testid={tid}
      onError={() => setErrored(true)}
    />
  );
}

function CategoryIcon({ category, className }) {
  const Icon = (() => {
    switch (category) {
      case "cigar_lounge":
      case "shop":
        return Cigarette;
      case "rooftop":
      case "bar":
      case "club":
      case "restaurant":
        return Sparkles;
      default:
        return Crown;
    }
  })();
  return <Icon className={className} strokeWidth={1.25} />;
}

function YouTubeGallerySlide({ youtubeId, poster, active, idx, title, markActiveHero }) {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    if (!active || !youtubeId) return;
    let cancelled = false;
    const ensureApi = () => new Promise((resolve) => {
      if (window.YT && window.YT.Player) { resolve(window.YT); return; }
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => { prev && prev(); resolve(window.YT); };
      if (!document.getElementById("yt-iframe-api")) {
        const s = document.createElement("script");
        s.id = "yt-iframe-api";
        s.src = "https://www.youtube.com/iframe_api";
        s.async = true;
        document.head.appendChild(s);
      }
    });
    (async () => {
      const YT = await ensureApi();
      if (cancelled || !containerRef.current) return;
      playerRef.current = new YT.Player(containerRef.current, {
        videoId: youtubeId,
        playerVars: {
          autoplay: 1, mute: 1, loop: 1, controls: 0, playsinline: 1,
          rel: 0, modestbranding: 1, playlist: youtubeId,
          iv_load_policy: 3, disablekb: 1,
        },
        events: {
          onReady: (ev) => { try { ev.target.mute(); ev.target.playVideo(); } catch {/* noop */} },
        },
      });
    })();
    return () => {
      cancelled = true;
      try { playerRef.current?.destroy?.(); } catch {/* noop */}
      playerRef.current = null;
    };
  }, [active, youtubeId]);

  const toggleMute = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const p = playerRef.current;
    if (!p) return;
    if (muted) { try { p.unMute(); } catch {/* noop */} setMuted(false); }
    else { try { p.mute(); } catch {/* noop */} setMuted(true); }
  };

  return (
    <div className="relative w-full place-profile-hero overflow-hidden" data-testid={markActiveHero ? "hero-photo" : undefined}>
      {!active && poster && (
        <img src={poster} alt={title} loading="lazy" className="absolute inset-0 w-full h-full object-cover" data-testid={`hero-yt-poster-${idx}`} />
      )}
      {active && (
        <div className="absolute inset-0 pointer-events-none" data-testid={`hero-yt-${idx}`}>
          <div ref={containerRef} className="w-full h-full [&>iframe]:w-full [&>iframe]:h-full [&>iframe]:block" />
        </div>
      )}
      {active && (
        <button
          type="button"
          onClick={toggleMute}
          aria-label={muted ? "Unmute" : "Mute"}
          data-testid={`hero-yt-mute-${idx}`}
          className="absolute bottom-14 right-3 z-20 w-11 h-11 grid place-items-center rounded-full bg-black/60 backdrop-blur-sm border border-white/15 text-white hover:text-gold transition-colors"
        >
          {muted ? <VolumeX className="w-4 h-4" strokeWidth={1.5}/> : <Volume2 className="w-4 h-4" strokeWidth={1.5}/>}
        </button>
      )}
    </div>
  );
}


/* ====================== iter69 helpers ====================== */
function OutlineActionBtn({ testId, icon, label, onClick, active }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className="flex flex-col items-center justify-center gap-1 transition-colors hover:bg-[rgba(232,184,75,0.06)]"
      style={{
        minHeight: 44,
        background: active ? "rgba(232,184,75,0.1)" : "transparent",
        border: active ? "1px solid rgba(232,184,75,0.8)" : "1px solid rgba(232,184,75,0.35)",
        borderRadius: 10,
        color: "#E8B84B",
        padding: "6px 4px",
        fontFamily: "'DM Sans', sans-serif",
      }}
      aria-pressed={active}
    >
      {icon}
      <span style={{ fontSize: 9, letterSpacing: "0.3px" }}>{label}</span>
    </button>
  );
}

function WaIcon() {
  return (
    <svg className="inline-block" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden style={{ color: "inherit" }}>
      <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448L.057 24z" />
    </svg>
  );
}

function ContactRow({ icon, iconBg, iconColor, label, value, actionLabel, actionGreen, href, onAction, testId }) {
  const Wrapper = href ? "a" : "div";
  const wrapperProps = href ? { href, target: href.startsWith("http") ? "_blank" : undefined, rel: "noopener noreferrer" } : {};
  return (
    <div
      className="flex items-center gap-3 py-3"
      style={{ borderBottom: "0.5px solid #1a1a1a", minHeight: 48 }}
      data-testid={testId}
    >
      <span
        className="grid place-items-center shrink-0"
        style={{ width: 28, height: 28, borderRadius: 7, background: iconBg, color: iconColor }}
      >
        {icon}
      </span>
      <div className="flex-1 min-w-0">
        <div className="truncate" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 9, color: "#B0AAA3", letterSpacing: "0.16em", textTransform: "uppercase", marginBottom: 2 }}>
          {label}
        </div>
        <div className="truncate" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#F5F1E8" }}>
          {value}
        </div>
      </div>
      <Wrapper
        {...wrapperProps}
        onClick={onAction}
        className="shrink-0 inline-flex items-center justify-center transition-opacity hover:opacity-80"
        style={{
          padding: "3px 7px",
          borderRadius: 5,
          border: `1px solid ${actionGreen ? "rgba(37,211,102,0.45)" : "rgba(232,184,75,0.45)"}`,
          color: actionGreen ? "#25d366" : "#E8B84B",
          background: "transparent",
          fontFamily: "'DM Sans', sans-serif",
          fontSize: 10,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          cursor: "pointer",
        }}
      >
        {actionLabel}
      </Wrapper>
    </div>
  );
}

function HoursBadge({ hours, openingHours, tI18n }) {
  const [open, setOpen] = useState(false);
  // Accept either:
  //   • array shape   [{day:0..6, open:"HH:MM", close:"HH:MM"}, ...]
  //   • object shape  { monday: {open:bool, open_time, close_time}, ... }
  const days = normalizeOpeningHours(openingHours);
  const hasStructured = Array.isArray(days) && days.length > 0;

  // No structured hours and no legacy text → render "Hours not available"
  if (!hasStructured && !hours) {
    return (
      <div className="mt-6" data-testid="hours-badge-unavailable">
        <div style={{ background: "#141414", border: "0.5px solid #222", borderRadius: 12, padding: "9px 12px" }}>
          <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#B0AAA3" }}>
            {tI18n("pp_hours_unavailable")}
          </span>
        </div>
      </div>
    );
  }

  const status = hasStructured ? computeStatus(days) : { isOpen: false, subText: "" };
  const dotColor = status.isOpen ? "#4ade80" : "#ef4444";
  const labelColor = status.isOpen ? "#4ade80" : "#ef4444";
  const labelText = status.isOpen ? tI18n("pp_hours_open") : tI18n("pp_hours_closed");

  return (
    <div className="mt-6" data-testid="hours-badge">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between"
        style={{
          background: "#141414",
          border: status.isOpen ? "0.5px solid rgba(74,222,128,0.25)" : "0.5px solid rgba(255,80,80,0.25)",
          borderRadius: 12,
          padding: "9px 12px",
          minHeight: 48,
          borderBottomLeftRadius: open ? 0 : 12,
          borderBottomRightRadius: open ? 0 : 12,
        }}
      >
        <span className="flex items-center gap-2.5">
          <span style={{ width: 7, height: 7, borderRadius: 999, background: dotColor, boxShadow: `0 0 6px ${dotColor}` }} />
          <span className="flex flex-col items-start">
            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 500, color: labelColor }}>
              {labelText}
            </span>
            {status.subText && (
              <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#9A9490", marginTop: 1 }}>
                {status.subText}
              </span>
            )}
            {!status.subText && hours && (
              <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#9A9490", marginTop: 1 }}>
                {hours}
              </span>
            )}
          </span>
        </span>
        <span className="inline-flex items-center gap-1 text-gold" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase" }}>
          {tI18n("pp_hours_all")}
          <ChevronDown className="w-3.5 h-3.5 transition-transform" style={{ transform: open ? "rotate(180deg)" : "none" }} strokeWidth={1.5} />
        </span>
      </button>
      {open && days && (
        <div
          style={{
            background: "#141414",
            border: "0.5px solid #222",
            borderTop: "none",
            borderRadius: "0 0 10px 10px",
            padding: "8px 12px",
          }}
        >
          {weekdayRows(days, tI18n)}
        </div>
      )}
      {open && !days && (
        <div
          style={{
            background: "#141414",
            border: "0.5px solid #222",
            borderTop: "none",
            borderRadius: "0 0 10px 10px",
            padding: "8px 12px",
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 11,
            color: "#F5F1E8",
          }}
        >
          {hours || ""}
        </div>
      )}
    </div>
  );
}

function computeStatus(days) {
  if (!days || !days.length) return { isOpen: false, subText: "" };
  // Compute current time in Havana (UTC-5, no DST).
  const now = new Date();
  const havanaMs = now.getTime() + (now.getTimezoneOffset() * 60 * 1000) - (5 * 60 * 60 * 1000);
  const havana = new Date(havanaMs);
  const dow = havana.getDay();
  const cur = havana.getHours() * 60 + havana.getMinutes();
  // Today's row
  const todayRow = days.find((d) => d.day === dow);
  if (todayRow && todayRow.open && todayRow.close) {
    const [oh, om] = todayRow.open.split(":").map(Number);
    const [ch, cm] = todayRow.close.split(":").map(Number);
    const openMin = oh * 60 + (om || 0);
    let closeMin = ch * 60 + (cm || 0);
    const overnight = closeMin <= openMin;
    if (overnight) closeMin += 24 * 60; // next-day close
    if (cur >= openMin && cur < closeMin) {
      return { isOpen: true, subText: `Closes ${todayRow.close}` };
    }
    if (cur < openMin) {
      return { isOpen: false, subText: `Opens ${todayRow.open}` };
    }
  }
  // Carried-over from yesterday's overnight?
  const yesterdayDow = (dow + 6) % 7;
  const yRow = days.find((d) => d.day === yesterdayDow);
  if (yRow && yRow.open && yRow.close) {
    const [oh, om] = yRow.open.split(":").map(Number);
    const [ch, cm] = yRow.close.split(":").map(Number);
    const openMin = oh * 60 + (om || 0);
    const closeMin = ch * 60 + (cm || 0);
    if (closeMin <= openMin && cur < closeMin) {
      return { isOpen: true, subText: `Closes ${yRow.close}` };
    }
  }
  return { isOpen: false, subText: "" };
}

// Convert various shapes of opening_hours into [{day, open, close}]
function normalizeOpeningHours(input) {
  if (!input) return null;
  if (Array.isArray(input)) return input;
  if (typeof input !== "object") return null;
  const SUN_INDEX = { monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6, sunday: 0 };
  const out = [];
  for (const key of Object.keys(SUN_INDEX)) {
    const row = input[key];
    if (row && row.open && row.open_time && row.close_time) {
      out.push({ day: SUN_INDEX[key], open: row.open_time, close: row.close_time });
    }
  }
  return out;
}

function weekdayRows(days, tI18n) {
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const today = new Date().getDay();
  return dayNames.map((name, idx) => {
    const row = days.find((d) => d.day === idx);
    const isToday = idx === today;
    const time = row && row.open && row.close ? `${row.open}–${row.close}` : tI18n("pp_hours_closed_short");
    return (
      <div
        key={idx}
        className="flex items-center justify-between"
        style={{
          padding: "3px 0",
          fontFamily: "'DM Sans', sans-serif",
          fontSize: 11,
          color: isToday ? "#E8B84B" : "#B0AAA3",
          fontWeight: isToday ? 500 : 400,
        }}
      >
        <span>{name}</span>
        <span style={{ color: isToday ? "#E8B84B" : "#F5F1E8" }}>{time}</span>
      </div>
    );
  });
}

function NearbySection({ bizId, category, neighborhood, tI18n, userCoords }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    let cancelled = false;
    // iter122 — Skip the network round-trip offline so iOS never raises
    // the cellular-data dialog.
    if (typeof navigator !== "undefined" && navigator.onLine === false) return;
    api.get(`/businesses/${bizId}/nearby`, { params: { limit: 5 } })
      .then(({ data }) => { if (!cancelled) setItems(Array.isArray(data) ? data : []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [bizId]);
  if (items.length === 0) return null;
  // iter80: nearby endpoint tiers by category → neighborhood → any active,
  // so the result set mixes categories. Label by neighborhood (if known)
  // instead of the parent's category to avoid mislabelling.
  const locationLabel = neighborhood || (category ? category.replaceAll("_", " ") : "places");
  return (
    <section className="mt-10" data-testid="nearby-section">
      <div className="px-6 flex items-center justify-between mb-3">
        <span className="inline-flex items-center gap-1.5" style={{ color: "#E8B84B", fontFamily: "'DM Sans', sans-serif", fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase" }}>
          <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} />
          {tI18n("pp_nearby_prefix")} {locationLabel}
        </span>
        <Link
          to={neighborhood ? `/?neighborhood=${encodeURIComponent(neighborhood)}` : "/"}
          data-testid="nearby-see-all"
          className="inline-flex items-center gap-0.5"
          style={{ color: "#B0AAA3", fontFamily: "'DM Sans', sans-serif", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase" }}
        >
          {tI18n("pp_nearby_see_all")} <ChevronRight className="w-3 h-3" strokeWidth={2} />
        </Link>
      </div>
      <div className="px-6 flex gap-3 overflow-x-auto no-scrollbar pb-2">
        {items.map((nb) => <NearbyCard key={nb.id} b={nb} userCoords={userCoords} />)}
      </div>
    </section>
  );
}

function NearbyCard({ b, userCoords }) {
  const photo = (b.photos && b.photos[0]?.url) || b.cover_photo || null;
  const dist = userCoords && typeof b.lat === "number" && typeof b.lng === "number"
    ? haversineKm({ lat: userCoords.lat, lng: userCoords.lng }, { lat: b.lat, lng: b.lng })
    : null;
  return (
    <Link
      to={`/places/${b.id}`}
      data-testid={`nearby-card-${b.id}`}
      className="shrink-0 overflow-hidden hover:opacity-95 transition-opacity"
      style={{ minWidth: 100, background: "#141414", border: "0.5px solid #222", borderRadius: 11 }}
    >
      <div
        className="relative"
        style={{
          height: 56,
          background: photo ? `url(${photo}) center/cover` : "linear-gradient(135deg, #1a1410 0%, #0f0c0a 100%)",
        }}
      >
        {b.category && (
          <span
            className="absolute bottom-1 left-1"
            style={{
              fontSize: 9,
              color: "#E8B84B",
              background: "rgba(0,0,0,0.6)",
              padding: "2px 5px",
              borderRadius: 4,
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            {b.category.replaceAll("_", " ")}
          </span>
        )}
      </div>
      <div style={{ padding: "6px 7px" }}>
        <div className="truncate" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 500, color: "#F5F1E8" }}>
          {b.name || "Place"}
        </div>
        {b.ratings_average != null && (
          <div className="inline-flex items-center gap-0.5" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 10, color: "#E8B84B", marginTop: 1 }}>
            ★ {Number(b.ratings_average).toFixed(1)}
          </div>
        )}
        {dist != null && (
          <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 10, color: "#9A9490", marginTop: 1 }}>
            {fmtDistanceWalk(dist)}
          </div>
        )}
      </div>
    </Link>
  );
}
